import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import { ArrowLeft, TrendingUp, Users, DollarSign, BarChart3, Trophy, Target } from "lucide-react";
import { useNavigate } from "react-router-dom";

const EsportStats = () => {
  const navigate = useNavigate();

  // Placeholder data
  const overallStats = {
    totalMarkets: 156,
    totalVolume: "$2,458,340",
    activeUsers: "12,453",
    avgAccuracy: "68.5%"
  };

  const gameStats = [
    {
      game: "Counter-Strike 2",
      code: "CS",
      markets: 48,
      volume: "$845,230",
      avgOdds: "1.85",
      color: "bg-orange-500"
    },
    {
      game: "Dota 2",
      code: "DT",
      markets: 38,
      volume: "$652,180",
      avgOdds: "1.92",
      color: "bg-red-500"
    },
    {
      game: "League of Legends",
      code: "LL",
      markets: 42,
      volume: "$723,450",
      avgOdds: "1.78",
      color: "bg-blue-500"
    },
    {
      game: "Valorant",
      code: "VL",
      markets: 28,
      volume: "$237,480",
      avgOdds: "1.95",
      color: "bg-pink-500"
    }
  ];

  const topPerformers = [
    {
      market: "CS Team Liquid vs NAVI - IEM Katowice 2024",
      volume: "$45,230",
      bets: 342,
      outcome: "Resolved"
    },
    {
      market: "DT OG vs Team Secret - DPC Spring Tour",
      volume: "$38,920",
      bets: 287,
      outcome: "Resolved"
    },
    {
      market: "LL T1 vs Gen.G - LCK Spring Finals",
      volume: "$52,180",
      bets: 421,
      outcome: "Active"
    }
  ];

  const recentTrends = [
    { period: "Last 7 days", markets: 23, volume: "$156,340", change: "+12.5%" },
    { period: "Last 30 days", markets: 89, volume: "$642,180", change: "+8.3%" },
    { period: "Last 90 days", markets: 156, volume: "$2,458,340", change: "+15.7%" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8 mt-16">
        {/* Back Button & Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Esport Statistics
          </h1>
          <p className="text-lg text-muted-foreground">
            Track performance metrics and trends across all esport prediction markets
          </p>
        </div>

        {/* Overall Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
            <div className="flex items-center justify-between mb-2">
              <Target className="w-8 h-8 text-primary" />
            </div>
            <div className="text-3xl font-bold text-foreground mb-1">
              {overallStats.totalMarkets}
            </div>
            <div className="text-sm text-muted-foreground">
              Total Markets
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-foreground mb-1">
              {overallStats.totalVolume}
            </div>
            <div className="text-sm text-muted-foreground">
              Total Volume
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-blue-500" />
            </div>
            <div className="text-3xl font-bold text-foreground mb-1">
              {overallStats.activeUsers}
            </div>
            <div className="text-sm text-muted-foreground">
              Active Users
            </div>
          </Card>

          <Card className="p-6 bg-gradient-to-br from-purple-500/10 to-purple-500/5 border-purple-500/20">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
            <div className="text-3xl font-bold text-foreground mb-1">
              {overallStats.avgAccuracy}
            </div>
            <div className="text-sm text-muted-foreground">
              Avg Accuracy
            </div>
          </Card>
        </div>

        {/* Game Statistics */}
        <Card className="p-6 mb-8">
          <div className="flex items-center gap-2 mb-6">
            <Trophy className="w-5 h-5 text-primary" />
            <h2 className="text-2xl font-bold text-foreground">Performance by Game</h2>
          </div>
          <div className="space-y-4">
            {gameStats.map((game, index) => (
              <div key={index} className="flex items-center gap-4 p-4 rounded-lg bg-card/50 hover:bg-card transition-colors">
                <div className={`w-12 h-12 rounded-lg ${game.color} flex items-center justify-center text-white font-bold text-lg`}>
                  {game.code}
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-foreground">{game.game}</div>
                  <div className="text-sm text-muted-foreground">
                    {game.markets} markets • Avg odds: {game.avgOdds}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-lg text-foreground">{game.volume}</div>
                  <div className="text-xs text-muted-foreground">Total Volume</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Top Performing Markets */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="w-5 h-5 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Top Markets</h2>
            </div>
            <div className="space-y-4">
              {topPerformers.map((market, index) => (
                <div key={index} className="p-4 rounded-lg bg-card/50 hover:bg-card transition-colors">
                  <div className="font-medium text-foreground mb-2">{market.market}</div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-muted-foreground">
                      {market.bets} bets
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-semibold text-foreground">{market.volume}</span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        market.outcome === "Resolved" 
                          ? "bg-green-500/20 text-green-500" 
                          : "bg-primary/20 text-primary"
                      }`}>
                        {market.outcome}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Recent Trends */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-primary" />
              <h2 className="text-2xl font-bold text-foreground">Growth Trends</h2>
            </div>
            <div className="space-y-4">
              {recentTrends.map((trend, index) => (
                <div key={index} className="p-4 rounded-lg bg-card/50 hover:bg-card transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-foreground">{trend.period}</span>
                    <span className="text-green-500 font-semibold">{trend.change}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-muted-foreground">Markets</div>
                      <div className="font-semibold text-foreground">{trend.markets}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Volume</div>
                      <div className="font-semibold text-foreground">{trend.volume}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Note about placeholder data */}
        <Card className="p-6 bg-muted/50 border-dashed">
          <p className="text-sm text-muted-foreground text-center">
            📊 These are placeholder statistics. Real data will be populated from the subgraph once markets are active.
          </p>
        </Card>
      </main>
    </div>
  );
};

export default EsportStats;

